/**
 * Manifest cv — gestion multi-CV : liste, édition, lecture publique.
 *
 * @remarks
 * Trois contextes :
 * - `cv-list` : liste « Mes CV » (création, duplication, suppression)
 * - `cv` : dashboard d'adéquation candidat ↔ offre (modes rh/edit), scopé `:cvId`
 * - `cv-skills` : grille de compétences détaillée (charts, evidence), scopé `:cvId`
 *
 * + une route standalone hors contexte : `/cv/:cvId` (zone public-bare) —
 * page CV éditoriale partageable, design SAAS-430, sans shell.
 *
 * Édition en zone authentifiée (mes CV sont privés à éditer) ; seule la page
 * publique reste accessible sans login. Entrée sidebar « Mes CV ».
 */

import { BarChart2, BookOpen, FileUser, Info, Share2, SlidersHorizontal, User } from 'lucide-react';
import { CONTEXTS, lazyView, type FeatureManifest } from '@/app/registry';
import { cvAuthenticatedRoutes, cvBareRoutes } from './routes';

export const cvManifest: FeatureManifest = {
  id: 'cv',
  contexts: [CONTEXTS.CV_LIST, CONTEXTS.CV, CONTEXTS.CV_SKILLS],

  routes: [
    { zone: 'authenticated', routes: cvAuthenticatedRoutes, errorFallbackLabel: 'CV' },
    { zone: 'public-bare', routes: cvBareRoutes, errorFallbackLabel: 'CV Public' },
  ],

  navigation: [
    {
      id: 'cv',
      label: 'CV',
      icon: FileUser,
      to: '/cv',
      position: 'top',
      order: 30,
    },
  ],

  centerView: {
    [CONTEXTS.CV_LIST]: lazyView(() => import('./views/CvListView'), 'CvListView'),
    [CONTEXTS.CV]: lazyView(() => import('./views/DashboardCenterView'), 'DashboardCenterView'),
    [CONTEXTS.CV_SKILLS]: lazyView(
      () => import('./views/SkillGridCenterView'),
      'SkillGridCenterView',
    ),
  },

  panels: {
    left: {
      tabs: {
        [CONTEXTS.CV_SKILLS]: [
          {
            id: 'skill-grid-candidate',
            label: 'Candidat',
            icon: User,
            view: lazyView(
              () => import('./views/SkillGridLeftCandidateView'),
              'SkillGridLeftCandidateView',
            ),
          },
          {
            id: 'skill-grid-filters',
            label: 'Filtres',
            icon: SlidersHorizontal,
            view: lazyView(
              () => import('./views/SkillGridLeftFiltersView'),
              'SkillGridLeftFiltersView',
            ),
          },
        ],
      },
    },
    right: {
      tabs: {
        [CONTEXTS.CV_SKILLS]: [
          {
            id: 'skill-grid-synthesis',
            label: 'Synthèse',
            icon: BarChart2,
            view: lazyView(
              () => import('./views/SkillGridRightSynthesisView'),
              'SkillGridRightSynthesisView',
            ),
          },
          {
            id: 'skill-grid-detail',
            label: 'Détail',
            icon: Info,
            view: lazyView(
              () => import('./views/SkillGridRightDetailView'),
              'SkillGridRightDetailView',
            ),
          },
          {
            id: 'skill-grid-guide',
            label: 'Guide',
            icon: BookOpen,
            view: lazyView(
              () => import('./views/SkillGridRightGuideView'),
              'SkillGridRightGuideView',
            ),
          },
          {
            id: 'skill-grid-share',
            label: 'Partager',
            icon: Share2,
            view: lazyView(
              () => import('./views/SkillGridRightShareView'),
              'SkillGridRightShareView',
            ),
          },
        ],
      },
    },
  },

  panelRequirements: {
    // Liste pleine largeur : pas de panneaux.
    [CONTEXTS.CV_LIST]: {
      left: { open: false },
      right: { open: false },
    },
    [CONTEXTS.CV]: {
      left: { open: false },
      right: { open: false },
    },
    [CONTEXTS.CV_SKILLS]: {
      left: { open: true, defaultActiveTab: 'skill-grid-candidate' },
      right: { open: true, defaultActiveTab: 'skill-grid-synthesis' },
    },
  },
};
